import type { DiscountConfiguration } from "../db";

export const HEADER_DISCOUNT_FIELD = "headerDiscount" as const;
export const IS_HEADER_DISCOUNT_PERCENTAGE_FIELD = "isHeaderDiscountPercentage" as const;
export const HEADER_DISCOUNT_GROUP_FIELD = "headerDiscountGroup" as const;

type PaymentTermWithGroup = {
  value: string;
  group?: string | null;
};

type DiscountConfigurationMatch =
  | DiscountConfiguration
  | DiscountConfiguration[]
  | null
  | undefined;

export const findMatchingDiscountConfiguration = (
  paymentTermValue: string,
  paymentTerms: PaymentTermWithGroup[],
  discountConfigurations: DiscountConfiguration[],
  headerDiscountGroup?: string | null,
): DiscountConfiguration | null => {
  const selectedTerm = paymentTerms.find((pt) => pt.value === paymentTermValue);
  const group = headerDiscountGroup?.trim() ? headerDiscountGroup : selectedTerm?.group;
  if (!group) return null;
  return discountConfigurations.find((dc) => dc.accountRelation === group) ?? null;
};

export const getHeaderDiscountGroup = (
  paymentTermValue: string,
  paymentTerms: PaymentTermWithGroup[],
): string => {
  const selectedTerm = paymentTerms.find((pt) => pt.value === paymentTermValue);
  return selectedTerm?.group ?? "";
};

export const getDiscountConfigurationForDefault = (
  matchingConfig: DiscountConfigurationMatch,
): DiscountConfiguration | null => {
  if (Array.isArray(matchingConfig)) return matchingConfig[0] ?? null;
  return matchingConfig ?? null;
};

export const hasMatchingDiscountConfiguration = (
  matchingConfig: DiscountConfigurationMatch,
): boolean => {
  if (Array.isArray(matchingConfig)) return matchingConfig.length > 0;
  return !!matchingConfig;
};

export const getHeaderDiscountDefault = (
  matchingConfig: DiscountConfigurationMatch,
): {
  field: typeof HEADER_DISCOUNT_FIELD;
  modeField: typeof IS_HEADER_DISCOUNT_PERCENTAGE_FIELD;
  value: string;
  showPercentIndicator: boolean;
} => {
  const config = getDiscountConfigurationForDefault(matchingConfig);
  const hasDiscountPercentage =
    config?.discountPercentage1 != null && String(config.discountPercentage1).trim() !== "";
  if (hasDiscountPercentage) {
    return {
      field: HEADER_DISCOUNT_FIELD,
      modeField: IS_HEADER_DISCOUNT_PERCENTAGE_FIELD,
      value: String(config.discountPercentage1),
      showPercentIndicator: true
    };
  }

  const hasDiscountAmount = config?.discountAmount != null && String(config.discountAmount).trim() !== "";
  if (hasDiscountAmount) {
    return {
      field: HEADER_DISCOUNT_FIELD,
      modeField: IS_HEADER_DISCOUNT_PERCENTAGE_FIELD,
      value: String(config.discountAmount),
      showPercentIndicator: false
    };
  }

  return {
    field: HEADER_DISCOUNT_FIELD,
    modeField: IS_HEADER_DISCOUNT_PERCENTAGE_FIELD,
    value: "0",
    showPercentIndicator: true
  };
};
